import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useLenis from "../../lib/useLenis";
import { getProject, getNextProject } from "../../data/projects";
import { optimizeImage } from "../../lib/optimizeImage";
import ProjectCarousel from "./ProjectCarousel";

const ease = [0.16, 1, 0.3, 1];

const Eyebrow = ({ children }) => (
    <span className="inline-block font-body font-semibold uppercase tracking-[0.2em] text-[11px] text-[#1F4F8F]">
        {children}
    </span>
);

const SectionHeading = ({ eyebrow, title, lede }) => (
    <div className="max-w-3xl">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold leading-[1.08] tracking-tight text-[#0B1F3A] mt-3">
            {title}
        </h2>
        {lede && (
            <p className="mt-5 font-body text-lg md:text-xl text-[#3D4654] leading-relaxed">
                {lede}
            </p>
        )}
    </div>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.85, ease, delay }}
        className={className}
    >
        {children}
    </motion.div>
);

export const CaseStudyProduct = () => {
    useLenis();
    const { slug } = useParams();
    const navigate = useNavigate();
    const project = getProject(slug);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [slug]);

    if (!project) return null;
    const next = getNextProject(slug);

    return (
        <main
            className="relative bg-[#FBF8F1] text-[#0B1F3A] overflow-x-hidden"
            data-testid={`case-study-${project.slug}`}
        >
            {/* Top bar */}
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease }}
                className="fixed top-4 right-4 md:top-5 md:right-12 z-50"
            >
                <div className="flex items-center gap-1 rounded-full border border-[rgba(11,31,58,0.08)] bg-white/85 backdrop-blur-xl px-3 py-2 shadow-[0_10px_40px_-20px_rgba(11,31,58,0.25)]">
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        data-testid="case-study-back-home"
                        className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-full font-heading font-medium text-[15px] text-[#0B1F3A] whitespace-nowrap"
                    >
                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#1F4F8F]" />
                        Mona Saedi
                    </button>
                    <Link
                        to="/#work"
                        data-testid="case-study-back-work"
                        className="px-2 md:px-4 py-1.5 rounded-full text-sm font-body font-medium text-[#0B1F3A] hover:bg-[#F1EFEA] whitespace-nowrap"
                    >
                        ← All work
                    </Link>
                    <a
                        href="mailto:mona.saedi90@gmail.com"
                        data-testid="case-study-cta-email"
                        className="pill-btn ml-1 inline-flex items-center gap-1.5 rounded-full bg-[#0B1F3A] text-white px-4 py-2 text-sm font-body font-semibold whitespace-nowrap"
                    >
                        Say hi <span aria-hidden>→</span>
                    </a>
                </div>
            </motion.header>

            {/* HERO */}
            <section className="relative pt-32 md:pt-44 pb-12 md:pb-16">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <FadeUp>
                        <div className="flex items-center gap-3 flex-wrap">
                            <Eyebrow>Case study</Eyebrow>
                            <span className="text-[#0B1F3A]/30">·</span>
                            <span className="font-body text-sm text-[#3D4654]">
                                {project.year}
                            </span>
                        </div>
                    </FadeUp>

                    <motion.h1
                        initial={{ opacity: 0, y: 28 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease, delay: 0.1 }}
                        data-testid="case-study-title"
                        className="font-heading text-5xl sm:text-6xl lg:text-[88px] font-semibold leading-[1.02] tracking-tight max-w-5xl mt-6 text-[#0B1F3A]"
                    >
                        {project.title}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, ease, delay: 0.2 }}
                        data-testid="case-study-subtitle"
                        className="mt-5 font-heading text-2xl md:text-3xl font-light text-[#3D4654] max-w-3xl"
                    >
                        {project.subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, ease, delay: 0.34 }}
                        className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-8 border-t border-[rgba(11,31,58,0.1)] pt-8"
                        data-testid="case-study-meta"
                    >
                        {[
                            ["Company", project.client],
                            ["Industry", project.industry],
                            ["Role", project.role],
                            ["Year", project.year],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <p className="font-body uppercase tracking-[0.18em] text-[11px] text-[#5E6776]">
                                    {label}
                                </p>
                                <p className="font-heading font-medium text-base md:text-lg mt-1.5 text-[#0B1F3A] leading-snug">
                                    {value}
                                </p>
                            </div>
                        ))}
                    </motion.div>

                    {project.external && (
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.85, ease, delay: 0.42 }}
                            className="mt-10"
                        >
                            <a
                                href={project.external}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="case-study-visit-live"
                                className="pill-btn group inline-flex items-center gap-2.5 rounded-full bg-[#0B1F3A] text-white px-6 py-3 font-body font-semibold text-sm md:text-base"
                            >
                                <span className="relative inline-flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#52C16E] opacity-75 animate-ping" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#52C16E]" />
                                </span>
                                {project.ctaLabel || "Visit live site"}
                                <span
                                    aria-hidden
                                    className="inline-block transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                >
                                    ↗
                                </span>
                            </a>
                        </motion.div>
                    )}
                </div>
            </section>

            <ProjectCarousel
                images={[...new Set([project.cover, ...(project.solution?.images || [])].filter(Boolean))]}
                title={project.title}
            />

            {/* OVERVIEW */}
            <section className="py-14 md:py-20" data-testid="case-study-overview">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
                    <div className="lg:col-span-4">
                        <FadeUp>
                            <Eyebrow>Overview</Eyebrow>
                            <h2 className="font-heading text-3xl md:text-4xl font-semibold leading-[1.1] tracking-tight mt-3 text-[#0B1F3A]">
                                {project.overviewTitle || "Overview"}
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.1} className="lg:col-span-8 font-body text-lg md:text-xl text-[#3D4654] leading-relaxed space-y-6">
                        <p>{project.intro}</p>
                        <p>{project.secondary}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                            <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-white p-6">
                                <Eyebrow>Business goals</Eyebrow>
                                <ul className="mt-4 space-y-2.5 text-base text-[#0B1F3A]">
                                    {project.productGoals.business.map((g) => (
                                        <li key={g} className="flex gap-2.5">
                                            <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-[#1F4F8F] shrink-0" />
                                            {g}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-white p-6">
                                <Eyebrow>User goals</Eyebrow>
                                <ul className="mt-4 space-y-2.5 text-base text-[#0B1F3A]">
                                    {project.productGoals.user.map((g) => (
                                        <li key={g} className="flex gap-2.5">
                                            <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-[#52C16E] shrink-0" />
                                            {g}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <p className="text-base text-[#5E6776] border-t border-[rgba(11,31,58,0.1)] pt-6 mt-8">
                            <span className="font-semibold text-[#0B1F3A]">My responsibilities: </span>
                            {project.roleNote}
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* PROBLEM */}
            <section className="py-14 md:py-20 bg-white" data-testid="case-study-problem">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <FadeUp>
                        <SectionHeading
                            eyebrow="The problem"
                            title={project.problemTitle || "The problem"}
                            lede={project.problemLede}
                        />
                    </FadeUp>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-14">
                        {project.problemPoints.map((p, i) => (
                            <FadeUp key={p.title} delay={i * 0.08}>
                                <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-[#FBF8F1] p-7 h-full">
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading font-semibold text-3xl text-[#1F4F8F]">
                                            0{i + 1}
                                        </span>
                                        <span className="inline-block h-2 w-2 rounded-full bg-[#B8442E]" />
                                    </div>
                                    <p className="font-heading text-xl md:text-2xl font-semibold mt-5 leading-snug text-[#0B1F3A]">
                                        {p.title}
                                    </p>
                                    <p className="mt-3 text-base text-[#3D4654] leading-relaxed">
                                        {p.body}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* JOURNEY / DISCOVERY (conditional) */}
            {project.journey && (
                <section className="py-14 md:py-20" data-testid="case-study-journey">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <FadeUp>
                            <SectionHeading
                                eyebrow={project.journey.eyebrow || "Discovery"}
                                title={project.journey.title}
                                lede={project.journey.lede}
                            />
                        </FadeUp>

                        <div className="mt-14 space-y-8">
                            {project.journey.beforeImg && (
                                <FadeUp>
                                    <div className="rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)]">
                                        <img
                                            src={project.journey.beforeImg}
                                            alt={project.journey.beforeAlt || "Before"}
                                            loading="lazy"
                                            className="w-full h-auto block"
                                        />
                                    </div>
                                </FadeUp>
                            )}
                            {project.journey.afterImg && (
                                <FadeUp delay={0.08}>
                                    <div className="rounded-3xl overflow-hidden border border-[rgba(31,79,143,0.18)]">
                                        <img
                                            src={project.journey.afterImg}
                                            alt={project.journey.afterAlt || "After"}
                                            loading="lazy"
                                            className="w-full h-auto block"
                                        />
                                    </div>
                                </FadeUp>
                            )}
                        </div>

                        {(project.journey.queries || project.journey.considerations) && (
                            <FadeUp delay={0.16}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-12">
                                    {project.journey.queries && (
                                        <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-white p-7">
                                            <Eyebrow>{project.journey.queries.title}</Eyebrow>
                                            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-[#0B1F3A]">
                                                {project.journey.queries.items.map((i) => (
                                                    <li key={i} className="flex items-start gap-2 text-sm md:text-base">
                                                        <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#1F4F8F] shrink-0" />
                                                        {i}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {project.journey.considerations && (
                                        <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-white p-7">
                                            <Eyebrow>{project.journey.considerations.title}</Eyebrow>
                                            <ul className="mt-5 grid grid-cols-1 gap-2.5 text-[#0B1F3A] text-sm md:text-base">
                                                {project.journey.considerations.items.map((c) => (
                                                    <li key={c} className="flex items-start gap-2">
                                                        <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#52C16E] shrink-0" />
                                                        {c}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </FadeUp>
                        )}
                    </div>
                </section>
            )}

            {/* PROCESS */}
            <section className="py-14 md:py-20 bg-white" data-testid="case-study-process">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <FadeUp>
                        <SectionHeading
                            eyebrow="Process"
                            title={project.processTitle || "Process"}
                        />
                    </FadeUp>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {project.process.map((p, i) => (
                            <FadeUp key={p.step} delay={(i % 2) * 0.08}>
                                <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-[#FBF8F1] p-7 h-full">
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading font-semibold text-4xl md:text-5xl text-[#1F4F8F]">
                                            {p.step}
                                        </span>
                                        <span className="inline-flex h-9 px-3 items-center rounded-full bg-white border border-[rgba(11,31,58,0.08)] text-[11px] font-body font-semibold uppercase tracking-[0.18em] text-[#5E6776]">
                                            Phase
                                        </span>
                                    </div>
                                    <p className="font-heading text-xl md:text-2xl font-semibold mt-5 leading-snug text-[#0B1F3A]">
                                        {p.title}
                                    </p>
                                    <p className="mt-3 text-base text-[#3D4654] leading-relaxed">
                                        {p.body}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>

                    {project.processVisual && (
                        <FadeUp delay={0.1}>
                            <div className="mt-12 rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)]">
                                <img
                                    src={project.processVisual}
                                    alt="Process visual"
                                    loading="lazy"
                                    className="w-full h-auto block"
                                />
                            </div>
                        </FadeUp>
                    )}
                </div>
            </section>

            {/* USER TESTING */}
            {project.testingFindings && project.testingFindings.length > 0 && (
                <section className="py-14 md:py-20" data-testid="case-study-testing">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <FadeUp>
                            <SectionHeading
                                eyebrow={project.testing?.eyebrow || "User testing"}
                                title={project.testing?.title || "What testing revealed, and what we changed."}
                                lede={project.testing?.lede}
                            />
                        </FadeUp>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                            {project.testingFindings.map((f, i) => (
                                <FadeUp key={f.title} delay={i * 0.08}>
                                    <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-white p-7 h-full">
                                        <Eyebrow>Finding 0{i + 1}</Eyebrow>
                                        <p className="font-heading text-xl md:text-2xl font-semibold mt-4 leading-snug text-[#0B1F3A]">
                                            {f.title}
                                        </p>
                                        <p className="mt-3 text-base text-[#3D4654] leading-relaxed">
                                            {f.body}
                                        </p>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FINAL SOLUTION */}
            {project.solution && (
                <section className="py-14 md:py-20 bg-white" data-testid="case-study-solution">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <FadeUp>
                            <SectionHeading
                                eyebrow={project.solution.eyebrow || "Final solution"}
                                title={project.solution.title}
                                lede={project.solution.lede}
                            />
                        </FadeUp>

                        {project.solution.images && project.solution.images.length > 0 && (
                            <FadeUp delay={0.1}>
                                {project.solution.layout === "centered" ? (
                                    <div className="mt-12 flex justify-center">
                                        <div className="rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)] bg-[#F4F1EA] flex items-center justify-center p-6 md:p-10 max-w-md">
                                            <img
                                                src={project.solution.images[0]}
                                                alt="Final solution visual"
                                                loading="lazy"
                                                className="w-full h-auto max-h-[700px] object-contain block"
                                            />
                                        </div>
                                    </div>
                                ) : project.solution.layout === "stack" ? (
                                    <div className="mt-12 space-y-6 md:space-y-8">
                                        {project.solution.images.map((img, i) => (
                                            <div
                                                key={img}
                                                className="rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)] bg-[#F4F1EA]"
                                            >
                                                <img
                                                    src={img}
                                                    alt={`Final solution visual ${i + 1}`}
                                                    loading="lazy"
                                                    className="w-full h-auto block"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6 md:gap-8 items-stretch">
                                        <div className="rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)] bg-[#F4F1EA]">
                                            <img
                                                src={project.solution.images[0]}
                                                alt="Final solution main visual"
                                                loading="lazy"
                                                className="w-full h-full object-cover object-top block"
                                            />
                                        </div>
                                        {project.solution.images[1] && (
                                            <div className="rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)] bg-[#F4F1EA] flex items-center justify-center p-6 md:p-8">
                                                <img
                                                    src={project.solution.images[1]}
                                                    alt="Final solution detail visual"
                                                    loading="lazy"
                                                    className="w-full max-h-[600px] h-auto object-contain block"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </FadeUp>
                        )}

                        {project.solution.highlights && project.solution.highlights.length > 0 && (
                            <FadeUp delay={0.16}>
                                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {project.solution.highlights.map((b) => (
                                        <div
                                            key={b.k}
                                            className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-[#FBF8F1] p-6"
                                        >
                                            <Eyebrow>{b.k}</Eyebrow>
                                            <p className="font-body text-base md:text-lg text-[#0B1F3A] leading-relaxed mt-3">
                                                {b.v}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </FadeUp>
                        )}
                    </div>
                </section>
            )}

            {/* IMPACT */}
            <section className="py-14 md:py-20 bg-[#0B1F3A] text-white" data-testid="case-study-impact">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <FadeUp>
                        <span className="inline-block font-body font-semibold uppercase tracking-[0.2em] text-[11px] text-[#9FB8DF]">
                            Impact
                        </span>
                    </FadeUp>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-6 items-end">
                        <FadeUp className="lg:col-span-7">
                            <p className="font-heading text-[110px] md:text-[180px] lg:text-[220px] font-semibold leading-none tracking-tight text-white">
                                {project.impactStat}
                            </p>
                            <p className="font-body text-lg md:text-xl text-[#A5B5CC] max-w-xl mt-4">
                                {project.impactLabel}
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.1} className="lg:col-span-5">
                            <ul className="space-y-4">
                                {project.outcomes.map((o) => (
                                    <li
                                        key={o}
                                        className="flex items-start gap-3 border-t border-white/15 pt-4"
                                    >
                                        <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-[#52C16E] shrink-0" />
                                        <span className="font-body text-base md:text-lg text-white/90">
                                            {o}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </FadeUp>
                    </div>

                    <FadeUp delay={0.18}>
                        <p className="mt-16 max-w-3xl font-body text-base md:text-lg text-[#A5B5CC] leading-relaxed">
                            {project.results}
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* LEARNINGS */}
            <section className="py-14 md:py-20" data-testid="case-study-learnings">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <FadeUp>
                        <SectionHeading
                            eyebrow="Key learnings"
                            title={project.learningsTitle || "What this project taught me."}
                        />
                    </FadeUp>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {project.learnings.map((l, i) => (
                            <FadeUp key={l.title} delay={(i % 2) * 0.08}>
                                <div className="rounded-2xl border border-[rgba(11,31,58,0.08)] bg-white p-7 h-full">
                                    <p className="font-heading text-xl md:text-2xl font-semibold leading-snug text-[#0B1F3A]">
                                        {l.title}
                                    </p>
                                    <p className="mt-3 text-base text-[#3D4654] leading-relaxed">
                                        {l.body}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>

                    {project.credits && (
                        <FadeUp delay={0.16}>
                            <p className="mt-14 text-sm md:text-base text-[#5E6776] border-t border-[rgba(11,31,58,0.1)] pt-6 max-w-3xl">
                                <span className="font-semibold text-[#0B1F3A]">
                                    Credits:{" "}
                                </span>
                                {project.credits}
                            </p>
                        </FadeUp>
                    )}
                </div>
            </section>

            {/* NEXT PROJECT */}
            <section className="py-14 md:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Eyebrow>Up next</Eyebrow>
                    <Link
                        to={`/work/${next.slug}`}
                        data-testid="case-study-next-project"
                        className="group mt-4 block relative rounded-3xl overflow-hidden border border-[rgba(11,31,58,0.08)]"
                    >
                        <div className="aspect-[16/8] md:aspect-[16/7] overflow-hidden bg-[#F4F1EA]">
                            <img
                                src={optimizeImage(next.cover, 1600)}
                                alt={next.title}
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.03]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/55 via-[#0B1F3A]/0 to-transparent" />
                        </div>
                        <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between text-white">
                            <div className="flex flex-wrap gap-2">
                                {next.tags.map((t) => (
                                    <span
                                        key={t}
                                        className="rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-body font-semibold text-[#0B1F3A]"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                            <div className="flex items-end justify-between gap-6">
                                <div>
                                    <p className="font-body text-white/80 text-sm uppercase tracking-[0.18em]">
                                        Next project
                                    </p>
                                    <h3 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold leading-tight mt-2">
                                        {next.title}
                                    </h3>
                                </div>
                                <span
                                    aria-hidden
                                    className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-[#0B1F3A] transition-transform duration-500 group-hover:rotate-45"
                                >
                                    ↗
                                </span>
                            </div>
                        </div>
                    </Link>

                    <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                        <Link
                            to="/#work"
                            data-testid="case-study-back-all-work"
                            className="pill-btn inline-flex items-center gap-2 rounded-full border border-[rgba(11,31,58,0.18)] bg-white px-6 py-3 font-body font-semibold text-[#0B1F3A]"
                        >
                            ← All projects
                        </Link>
                        <a
                            href="mailto:mona.saedi90@gmail.com"
                            data-testid="case-study-footer-email"
                            className="pill-btn inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white px-6 py-3 font-body font-semibold"
                        >
                            mona.saedi90@gmail.com <span aria-hidden>↗</span>
                        </a>
                    </div>
                </div>
            </section>

            <footer
                data-testid="case-study-footer"
                className="border-t border-[rgba(11,31,58,0.1)] py-8 font-body text-sm text-[#5E6776]"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <p>© {new Date().getFullYear()} Mona Saedi. All rights reserved.</p>
                    <span className="font-body text-xs uppercase tracking-[0.2em] text-[#1F4F8F]">
                        Case study
                    </span>
                </div>
            </footer>
        </main>
    );
};

export default CaseStudyProduct;
